import { Modal } from "@msys/ui";
import { DialogContentText, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { once, uniqueId } from "lodash-es";
import React from "react";
import { ImplementsTemplateTypePropertiesStrategy } from "../../../../clients/graphqlTypes.js";
import { CheckboxField } from "../../../commons/form-fields/CheckboxField.js";
import { RadioGroupField } from "../../../commons/form-fields/RadioGroupField.js";

interface FormValues {
  strategy: ImplementsTemplateTypePropertiesStrategy;
  mergeIdenticalProperties: boolean;
}

// Add -------------------------------------------------------------------------

/**
 * Overrides for callers where the default copy would be untrue — a dialog that
 * only *stages* the chosen strategy has saved nothing yet, so it cannot promise
 * "Save and apply".
 */
export interface TemplateTypePropertiesStrategyLabels {
  title?: string;
  confirmButtonLabel?: string;
}

interface TemplateTypeAddPropertiesStrategyModalProps extends TemplateTypePropertiesStrategyLabels {
  handleComplete?: (strategy: ImplementsTemplateTypePropertiesStrategy) => void;
  handleClose: () => void;
}

export const TemplateTypeAddPropertiesStrategyModal = ({
  title,
  confirmButtonLabel,
  handleComplete,
  handleClose,
}: TemplateTypeAddPropertiesStrategyModalProps) => {
  const { t } = useTranslate(["Global", "TemplateTypes"]);
  const formId = React.useMemo(() => uniqueId(), []);
  return (
    <Formik<FormValues>
      onSubmit={async values => {
        handleComplete?.(
          values.strategy === "keepProperties" &&
            values.mergeIdenticalProperties
            ? "keepPropertiesWithMerging"
            : values.strategy
        );
        handleClose();
      }}
      enableReinitialize
      initialValues={{
        strategy: "discardProperties",
        mergeIdenticalProperties: true,
      }}
    >
      {formikProps => (
        <Modal
          title={
            title ??
            t("Adjust item properties", {
              ns: "TemplateTypes",
            })
          }
          handleClose={handleClose}
          actionButtons={[
            {
              label: t("Cancel", { ns: "Global" }),
              handleClick: handleClose,
              buttonProps: { variant: "text" },
            },
            {
              label:
                confirmButtonLabel ??
                t("Save and apply template type", {
                  ns: "TemplateTypes",
                }),
              buttonProps: {
                form: formId,
                type: "submit",
              },
            },
          ]}
          notInStack
        >
          <Form id={formId}>
            <Stack direction="column" spacing={1}>
              <DialogContentText>
                {t(
                  "There appear to be some custom properties set up. Would you like to:",
                  {
                    ns: "TemplateTypes",
                  }
                )}
              </DialogContentText>
              <RadioGroupField
                name="strategy"
                options={
                  [
                    {
                      value: "discardProperties",
                      label: t(
                        "Discard all existing properties and only use template type properties",
                        {
                          ns: "TemplateTypes",
                        }
                      ),
                    },
                    {
                      value: "keepProperties",
                      label: t(
                        "Keep all old properties along with the template type properties",
                        {
                          ns: "TemplateTypes",
                        }
                      ),
                    },
                  ] as {
                    value: ImplementsTemplateTypePropertiesStrategy;
                    label: string;
                  }[]
                }
              />
              {formikProps.values.strategy === "keepProperties" && (
                <CheckboxField
                  name="mergeIdenticalProperties"
                  label={t("Automatically merge identical properties", {
                    ns: "TemplateTypes",
                  })}
                  size="small"
                  labelTypographyProps={{ variant: "body2" }}
                  sx={{ ml: 5.5 }}
                />
              )}
            </Stack>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};

// Remove ----------------------------------------------------------------------

interface TemplateTypeRemovePropertiesStrategyModalProps {
  handleComplete?: (strategy: ImplementsTemplateTypePropertiesStrategy) => void;
  handleClose: () => void;
}

export const TemplateTypeRemovePropertiesStrategyModal = ({
  handleComplete,
  handleClose,
}: TemplateTypeRemovePropertiesStrategyModalProps) => {
  const { t } = useTranslate(["Global", "TemplateTypes"]);
  const formId = React.useMemo(() => uniqueId(), []);
  return (
    <Formik<FormValues>
      onSubmit={async values => {
        handleComplete?.(values.strategy);
        handleClose();
      }}
      enableReinitialize
      initialValues={{
        strategy: "discardProperties",
        mergeIdenticalProperties: true,
      }}
    >
      <Modal
        title={t("Keep or remove template type properties", {
          ns: "TemplateTypes",
        })}
        handleClose={handleClose}
        actionButtons={[
          {
            label: t("Cancel", { ns: "Global" }),
            handleClick: handleClose,
            buttonProps: { variant: "text" },
          },
          {
            label: t("Remove template type", {
              ns: "TemplateTypes",
            }),
            buttonProps: {
              form: formId,
              type: "submit",
            },
          },
        ]}
        notInStack
      >
        <Form id={formId}>
          <Stack direction="column" spacing={1}>
            <DialogContentText>
              {t("Would you like to:", { ns: "TemplateTypes" })}
            </DialogContentText>
            <RadioGroupField
              name="strategy"
              options={
                [
                  {
                    value: "discardProperties",
                    label: t(
                      "Discard all properties of template type being removed",
                      { ns: "TemplateTypes" }
                    ),
                  },
                  {
                    value: "keepProperties",
                    label: t("Keep all properties", { ns: "TemplateTypes" }),
                  },
                ] as {
                  value: ImplementsTemplateTypePropertiesStrategy;
                  label: string;
                }[]
              }
            />
          </Stack>
        </Form>
      </Modal>
    </Formik>
  );
};

// Process ---------------------------------------------------------------------

export interface TemplateTypePropertiesProcessRef {
  start: (
    type: "add" | "remove",
    /** Only read by the "add" modal; see the interface's docs. */
    labels?: TemplateTypePropertiesStrategyLabels
  ) => Promise<ImplementsTemplateTypePropertiesStrategy | null>;
}

export const TemplateTypeRemovePropertiesProcess = React.memo(
  React.forwardRef(
    (_: {}, forwardedRef: React.Ref<TemplateTypePropertiesProcessRef>) => {
      const [type, setType] = React.useState<"add" | "remove" | null>(null);
      const [labels, setLabels] =
        React.useState<TemplateTypePropertiesStrategyLabels>({});

      const resolveRef = React.useRef<
        | ((value: ImplementsTemplateTypePropertiesStrategy | null) => void)
        | null
      >(null);

      React.useImperativeHandle(forwardedRef, () => ({
        start: (
          type: "add" | "remove",
          labels?: TemplateTypePropertiesStrategyLabels
        ) =>
          new Promise<ImplementsTemplateTypePropertiesStrategy | null>(
            resolve => {
              resolveRef.current = once(resolve);
              setLabels(labels ?? {});
              setType(type);
            }
          ),
      }));

      return type === "add" ? (
        <TemplateTypeAddPropertiesStrategyModal
          title={labels.title}
          confirmButtonLabel={labels.confirmButtonLabel}
          handleClose={() => {
            setType(null);
            resolveRef.current?.(null);
          }}
          handleComplete={strategy => {
            resolveRef.current?.(strategy);
          }}
        />
      ) : type === "remove" ? (
        <TemplateTypeRemovePropertiesStrategyModal
          handleClose={() => {
            setType(null);
            resolveRef.current?.(null);
          }}
          handleComplete={strategy => {
            resolveRef.current?.(strategy);
          }}
        />
      ) : null;
    }
  )
);
